import React, { useMemo, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit2, Trash2, RefreshCw, Package, MapPin, Tag as TagIcon, X, Layers } from 'lucide-react';
import { API_URL } from '../api/client';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// --- Helper Functions ---
const getFullImageUrl = (item) => {
  if (item && item.local_image_blob) {
    return URL.createObjectURL(item.local_image_blob);
  }
  if (!item || !item.image_path) return null;
  const filename = item.image_path.split(/[/\\]/).pop();
  return `${API_URL}/uploads/${filename}`;
};

// --- Memoized Components ---

const GridItem = memo(({ item, styles, darkMode, gridAspectRatio, handlePressStart, handlePressEnd, onClick, handleDelete }) => {
  const imageUrl = getFullImageUrl(item);
  
  return (
    <motion.div 
      variants={itemVariants}
      layoutId={`card-${item.id}`}
      style={styles.gridCard} 
      onClick={onClick}
      onPointerDown={() => handlePressStart(item)}
      onPointerUp={handlePressEnd}
      onPointerLeave={handlePressEnd}
      whileTap={{ scale: 0.95 }}
    >
      <div style={{
        ...styles.gridMedia,
        aspectRatio: gridAspectRatio.replace('/', '/'),
      }}>
        {imageUrl ? (
          <img 
            src={`${imageUrl}?v=${item.updated_at}`} 
            alt={item.name} 
            style={styles.gridMediaImg} 
            loading="lazy" 
            decoding="async"
          />
        ) : (
          <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', color: '#ccc', fontSize: '12px'}}>No Image</div>
        )}
        
        {/* Overlay Badges */}
        <div style={styles.gridActions}>
            <button 
              onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
              style={{...styles.gridActionBtn, color: '#ff4d4d'}}
            >
              <Trash2 size={14} />
            </button>
        </div>
        
        <div style={styles.gridOverlayQtyBadge}>
          {item.quantity}개
        </div>
      </div>
      
      <div style={styles.gridMeta}>
        <div style={styles.gridMetaTop}>
          <div style={{fontWeight: '600', fontSize: '14px', color: darkMode ? '#eee' : '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1}}>
            {item.name}
          </div>
        </div>
        
        <div style={styles.gridPillRow}>
            {item.category && (
              <span style={{...styles.gridTagPill, backgroundColor: darkMode ? '#1a3a5a' : '#e3f2fd', color: darkMode ? '#8ab4f8' : '#1976d2'}}>
                {item.category}
              </span>
            )}
            <span style={{...styles.gridTagPill, backgroundColor: darkMode ? '#333' : '#eee', color: darkMode ? '#ccc' : '#666'}}>
              {item.location}
            </span>
            {item.tags && item.tags.split(',').slice(0, 2).map((tag, idx) => (
              <span key={idx} style={{...styles.gridTagPill, backgroundColor: darkMode ? '#2d2d2d' : '#f5f5f5', color: darkMode ? '#aaa' : '#888', border: darkMode ? '1px solid #444' : '1px solid #ddd'}}>
                #{tag.trim()}
              </span>
            ))}
        </div>
      </div>
    </motion.div>
  );
});

const ListItem = memo(({ item, styles, darkMode, handlePressStart, handlePressEnd, onClick, handleDelete }) => {
  const imageUrl = getFullImageUrl(item);

  return (
    <motion.div 
      variants={itemVariants}
      layoutId={item.id}
      style={styles.itemCard}
      whileTap={{ scale: 0.98 }}
      onPointerDown={() => handlePressStart(item)}
      onPointerUp={handlePressEnd}
      onPointerLeave={handlePressEnd}
      onClick={onClick}
    >
      <div style={styles.thumbnailContainer}>
        {imageUrl ? (
          <img 
            src={`${imageUrl}?v=${item.updated_at}`} 
            alt={item.name} 
            style={styles.itemThumbnail} 
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div style={{...styles.itemThumbnail, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '10px'}}>No Image</div>
        )}
      </div>
      
      <div style={styles.itemInfo}>
        <h3 style={styles.itemName}>{item.name}</h3>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
          <span style={styles.categoryBadge}>{item.category || '미분류'}</span>
          <span style={{ fontSize: '12px', color: '#666' }}>{item.location} • {item.quantity}개</span>
          {item.tags && item.tags.split(',').map((tag, idx) => (
            <span key={idx} style={{ 
              fontSize: '11px', 
              color: darkMode ? '#aaa' : '#888', 
              backgroundColor: darkMode ? '#333' : '#f5f5f5',
              padding: '1px 6px',
              borderRadius: '4px'
            }}>
              #{tag.trim()}
            </span>
          ))}
        </div>
      </div>
      
      <div style={styles.actionBtns}>
        <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} style={styles.deleteBtn}>
          <Trash2 size={18} />
        </button>
      </div>
    </motion.div>
  );
});

const ItemListView = ({ 
  items, 
  loading, 
  viewMode, 
  gridColumns, 
  gridDensity, 
  gridAspectRatio, 
  viewportWidth, 
  handleEdit, 
  handleDelete, 
  fetchItems,
  styles,
  darkMode
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [pressTimer, setPressTimer] = useState(null);
  const [longPressed, setLongPressed] = useState(false);

  // Lightbox States for Fullscreen Image Zoom
  const [showLightbox, setShowLightbox] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [touchStartDist, setTouchStartDist] = useState(0);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchStartDist(dist);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDist > 0) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const factor = dist / touchStartDist;
      const newScale = Math.min(Math.max(1, zoomScale * factor), 5);
      setZoomScale(newScale);
      setTouchStartDist(dist);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartDist(0);
  };

  const isGrid = viewMode === 'grid' || viewMode === 'gallery';

  const handlePressStart = (item) => {
    setLongPressed(false);
    const timer = setTimeout(() => {
      setLongPressed(true);
      if (window.navigator.vibrate) window.navigator.vibrate(50);
      handleEdit(item);
    }, 600); // 600ms for long press
    setPressTimer(timer);
  };

  const handlePressEnd = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  const gridGap = gridDensity === 'compact' ? 10 : gridDensity === 'spacious' ? 20 : 15;
  const containerWidth = Math.min(viewportWidth, 600);
  const maxColumnsForWidth = containerWidth < 360 ? 2 : containerWidth < 520 ? 2 : containerWidth < 720 ? 3 : containerWidth < 960 ? 4 : 5;
  
  const effectiveColumns = useMemo(() => {
    if (gridColumns === 'auto') return maxColumnsForWidth;
    const parsed = Number(gridColumns);
    if (!Number.isFinite(parsed)) return maxColumnsForWidth;
    return Math.max(1, Math.min(parsed, maxColumnsForWidth));
  }, [gridColumns, maxColumnsForWidth]);

  if (loading) {
    return (
      <div style={{textAlign: 'center', padding: '50px', color: '#666'}}>
        <div style={{display: 'inline-block', width: '30px', height: '30px', border: '3px solid rgba(0,0,0,0.1)', borderTop: '3px solid #000', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '15px'}}></div>
        <div>Loading...</div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={styles.emptyMsg}>
        <div style={{marginBottom: '15px'}}>검색 결과가 없습니다.</div>
        <button onClick={fetchItems} style={styles.retryBtn}>
          <RefreshCw size={14} style={{marginRight: '5px'}} />
          전체 목록 보기
        </button>
      </div>
    );
  }

  // Common props for items
  const itemProps = {
    styles,
    darkMode,
    handlePressStart,
    handlePressEnd,
    handleDelete
  };

  return (
    <>
      {isGrid ? (
        <motion.div 
          key="grid-view"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            ...styles.itemList,
            display: 'grid',
            gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
            gap: `${gridGap}px`,
            padding: '15px',
            paddingBottom: '100px'
          }}
        >
          {items.map((item) => (
            <GridItem
              key={item.id}
              item={item}
              gridAspectRatio={gridAspectRatio}
              onClick={() => {
                if (!longPressed) setSelectedItem(item);
              }}
              {...itemProps}
            />
          ))}
        </motion.div>
      ) : (
        <motion.div 
          key="list-view"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{...styles.itemList, paddingBottom: '100px'}}
        >
          {items.map((item) => (
            <ListItem
              key={item.id}
              item={item}
              onClick={() => {
                if (!longPressed) setSelectedItem(item);
              }}
              {...itemProps}
            />
          ))}
        </motion.div>
      )}

      {/* Modal for Detail View */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.8)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              backdropFilter: 'blur(5px)'
            }}
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              layoutId={`card-${selectedItem.id}`}
              style={{
                width: '100%',
                maxWidth: '400px',
                backgroundColor: darkMode ? '#2a2a2a' : '#fff',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                style={{ 
                  position: 'relative', 
                  width: '100%', 
                  aspectRatio: '4/3', 
                  backgroundColor: '#000', 
                  cursor: getFullImageUrl(selectedItem) ? 'zoom-in' : 'default' 
                }} 
                onClick={(e) => {
                  e.stopPropagation();
                  if (getFullImageUrl(selectedItem)) {
                    setShowLightbox(true);
                  }
                }}
              >
                {getFullImageUrl(selectedItem) ? (
                  <img 
                    src={`${getFullImageUrl(selectedItem)}?v=${selectedItem.updated_at}`} 
                    alt={selectedItem.name} 
                    style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }} 
                    decoding="async"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLightbox(true);
                    }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                    <Package size={48} opacity={0.3} />
                  </div>
                )}
                <button 
                  onClick={() => setSelectedItem(null)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: darkMode ? '#fff' : '#000' }}>
                    {selectedItem.name}
                  </h2>
                  <div style={{ 
                    padding: '6px 16px', 
                    backgroundColor: darkMode ? '#444' : '#f0f0f0', 
                    borderRadius: '99px', 
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }}>
                    {selectedItem.quantity}개
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: darkMode ? '#aaa' : '#666' }}>
                    <Layers size={18} />
                    <span style={{ fontWeight: '600' }}>카테고리:</span>
                    <span style={{ color: darkMode ? '#8ab4f8' : '#1976d2' }}>{selectedItem.category || '미지정'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: darkMode ? '#aaa' : '#666' }}>
                    <MapPin size={18} />
                    <span style={{ fontWeight: '600' }}>위치:</span>
                    <span>{selectedItem.location}</span>
                  </div>
                  {selectedItem.tags && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', color: darkMode ? '#aaa' : '#666' }}>
                      <TagIcon size={18} style={{ marginTop: '2px' }} />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {selectedItem.tags.split(',').map((tag, idx) => (
                          <span key={idx} style={{ 
                            padding: '2px 8px', 
                            backgroundColor: darkMode ? '#333' : '#f5f5f5', 
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            #{tag.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
                  <button 
                    onClick={() => { setSelectedItem(null); handleEdit(selectedItem); }}
                    style={{ 
                      flex: 1, 
                      padding: '12px', 
                      backgroundColor: '#000', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Edit2 size={18} /> 정보 수정
                  </button>
                  <button 
                    onClick={() => { if(window.confirm('삭제하시겠습니까?')) { setSelectedItem(null); handleDelete(selectedItem.id); } }}
                    style={{ 
                      padding: '12px', 
                      backgroundColor: darkMode ? '#333' : '#fee2e2', 
                      color: '#ef4444', 
                      border: 'none', 
                      borderRadius: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox for Fullscreen Image Zoom */}
      <AnimatePresence>
        {showLightbox && selectedItem && getFullImageUrl(selectedItem) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.95)',
              zIndex: 2000,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              touchAction: 'none'
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={() => {
              setShowLightbox(false);
              setZoomScale(1);
            }}
          >
            {/* Header Area */}
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '20px',
              paddingBottom: '10px',
              zIndex: 2010
            }} onClick={e => e.stopPropagation()}>
              <h4 style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                {selectedItem.name}
              </h4>
              <button
                onClick={() => {
                  setShowLightbox(false);
                  setZoomScale(1);
                }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Middle Image Area */}
            <div 
              style={{
                flex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative'
              }}
              onClick={e => e.stopPropagation()}
            >
              <motion.img
                key={zoomScale}
                src={`${getFullImageUrl(selectedItem)}?v=${selectedItem.updated_at}`}
                alt={selectedItem.name}
                drag={zoomScale > 1}
                dragConstraints={{
                  left: -200 * (zoomScale - 1),
                  right: 200 * (zoomScale - 1),
                  top: -200 * (zoomScale - 1),
                  bottom: 200 * (zoomScale - 1)
                }}
                dragElastic={0.15}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  scale: zoomScale,
                  cursor: zoomScale > 1 ? 'grab' : 'default',
                  touchAction: 'none'
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (zoomScale > 1) {
                    setZoomScale(1);
                  } else {
                    setZoomScale(2.5);
                  }
                }}
              />
            </div>

            {/* Bottom Slider & Buttons Panel */}
            <div 
              style={{
                width: '100%',
                maxWidth: '400px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
                zIndex: 2010,
                marginBottom: '20px'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <span style={{
                  color: '#fff',
                  fontSize: '12px',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontWeight: 'bold'
                }}>
                  배율: {Math.round(zoomScale * 100)}%
                </span>
                
                {zoomScale > 1 && (
                  <button
                    onClick={() => {
                      setZoomScale(1);
                    }}
                    style={{
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: '#0088FE',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    초기화
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '15px' }}>
                <button
                  onClick={() => setZoomScale(prev => Math.max(1, prev - 0.5))}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    border: 'none',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  -
                </button>
                
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="0.1"
                  value={zoomScale}
                  onChange={(e) => setZoomScale(parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    accentColor: '#0088FE',
                    height: '6px',
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                />

                <button
                  onClick={() => setZoomScale(prev => Math.min(5, prev + 0.5))}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    border: 'none',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  +
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ItemListView;